import React, { useEffect, useState, useMemo } from "react";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import {
  getAllCourseStructures,
  deleteCourseStructure,
  bulkSaveCourseStructures,
} from "../../../service/course-structure";
import { getAllClassSubjects } from "../../../service/class-subject";
import { getAllResultBreakdowns } from "../../../service/result-breakdown";
import { getAllSystems } from "../../../service/systems";
import { getAllClasses } from "../../../service/classes";
import { getAllSessions, type Session } from "../../../service/sessions";
import { listGroups } from "../../../service/group";
import {
  selectBranches,
  type Branch,
} from "../../../core/data/redux/slice/branchesSlice";

type System = { _id: string; name: string };
type ClassItem = { _id: string; name: string; order?: number; system?: string };
type Breakdown = { _id: string; title: string; startDate: string; endDate: string };
// A stream within a system, scoped to specific classes -- e.g. Biology vs
// Computer under Grade IX/X. Most classes have none; `classes` is how a
// class finds out which groups (if any) apply to it.
type GroupItem = { _id: string; name: string; system: string; classes: string[] };

type SubjectConfig = {
  structureId: string;
  // Every stored row for this subject — one per section of the class. Delete
  // must remove all of them, not just the first.
  structureIds: string[];
  courseId: string;
  courseName: string;
  courseCode: string;
  displayOrder: number;
  totalMarks: number;
  // The real rows, not just their sum — they are editable now, and the sum is
  // derived from them so the two can never drift apart.
  //
  // `_id` MUST be carried through on every existing row. Student marks are
  // stored against the embedded subcategory's _id (841 of 896 rows do this), so
  // saving without it makes Mongoose mint fresh ids and every one of those marks
  // is orphaned — gone from tabulation and report cards, with nothing to show it
  // happened. New rows have no _id and correctly get one on save.
  subCategories: { _id?: string; description: string; marks: number }[];
  subCategoriesTotal: number;
  termsPercentTotal: number;
  inTotal: boolean;
  gradeOnly: boolean;
};

type AllocationGroup = {
  key: string;
  breakdown: Breakdown;
  cls: ClassItem;
  classGroup: { _id: string; name: string } | null;
  subjects: SubjectConfig[];
  totalMarks: number;
  sessionId?: string;
  branchId?: string;
  systemId?: string;
};

type AddForm = {
  sessionId: string;
  branchId: string;
  breakdownId: string;
  classId: string;
  classGroupId: string;
  subjects: SubjectConfig[];
};

const EMPTY_FORM: AddForm = { sessionId: "", branchId: "", breakdownId: "", classId: "", classGroupId: "", subjects: [] };

const MarksAllocationPage = () => {
  const branches = useSelector(selectBranches);
  const [systems, setSystems] = useState<System[]>([]);
  const [allClasses, setAllClasses] = useState<ClassItem[]>([]);
  const [allGroups, setAllGroups] = useState<GroupItem[]>([]);
  const [breakdowns, setBreakdowns] = useState<Breakdown[]>([]);
  const [modalBreakdownList, setModalBreakdownList] = useState<Breakdown[]>([]);
  const [sessions, setSessions] = useState<Session[]>([]);
  const [allocations, setAllocations] = useState<AllocationGroup[]>([]);
  const [loading, setLoading] = useState(true);

  // Draft = what is currently picked in the bar. Applied = what Search
  // committed and what the list actually reflects. Keeping them apart is what
  // stops the page loading every allocation in the school on open, and matches
  // Defaulters and Fee Sticker Printing.
  const [draftBranch, setDraftBranch] = useState("");
  const [draftSession, setDraftSession] = useState("");
  const [draftSystem, setDraftSystem] = useState("");
  const [draftClass, setDraftClass] = useState("");
  const [draftGroup, setDraftGroup] = useState("");
  const [draftBreakdown, setDraftBreakdown] = useState("");
  const [appliedFilters, setAppliedFilters] = useState<{
    branch: string;
    session: string;
    system: string;
    class: string;
    classGroup: string;
    breakdown: string;
  } | null>(null);
  const [hasSearched, setHasSearched] = useState(false);
  // Distinguishes "no subjects loaded yet" from "loaded, and there are none",
  // so the empty-state message only shows once we actually know.
  const [subjectsLoaded, setSubjectsLoaded] = useState(false);
  const [page, setPage] = useState(1);
  const [limit] = useState(10);

  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState<"new" | "edit">("new");
  const [form, setForm] = useState<AddForm>(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [editingAllocation, setEditingAllocation] = useState<AllocationGroup | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<AllocationGroup | null>(null);

  // Filtering happens SERVER-side against the applied filters. It used to be
  // client-side over an unfiltered fetch, and only Class and Breakdown were
  // ever applied -- Session, Campus and Board were wired to state that nothing
  // read, so picking them changed nothing at all.
  const fetchAllocations = async (f = appliedFilters) => {
    if (!f) return;
    setLoading(true);
    try {
      // Page through until everything is in hand. The API caps limit at 100,
      // so the old single request for 500 silently received 100 and dropped
      // the rest -- and because rows are per section, one session at a
      // mid-sized campus easily exceeds that (Naseerabad needs ~216). The
      // effect was a class quietly showing fewer subjects than it has.
      const rows: any[] = [];
      let pageNo = 1;
      // Hard stop so a bad pagination response can never spin forever.
      while (pageNo <= 50) {
        const res = await getAllCourseStructures({
          limit: 100,
          page: pageNo,
          branch: f.branch || undefined,
          session: f.session || undefined,
          system: f.system || undefined,
          class: f.class || undefined,
          classGroup: f.classGroup || undefined,
          breakdown: f.breakdown || undefined,
        });
        const batch = (res.data as any[]) || [];
        rows.push(...batch);
        if (!res.pagination?.hasNextPage || !batch.length) break;
        pageNo += 1;
      }

      const map = new Map<string, AllocationGroup>();

      for (const cs of rows) {
        if (!cs.breakdown || !cs.class || !cs.course) continue;

        // classGroup in the key, not just class+breakdown -- otherwise a
        // Biology allocation and a Computer allocation for the same class
        // would merge into one card, each silently overwriting/mixing in
        // the other's subjects.
        const groupKey = cs.classGroup?._id || "none";
        const key = `${cs.breakdown._id}|${cs.class._id}|${groupKey}`;
        if (!map.has(key)) {
          map.set(key, {
            key,
            breakdown: cs.breakdown,
            cls: cs.class,
            classGroup: cs.classGroup || null,
            subjects: [],
            totalMarks: 0,
            sessionId: cs.session?._id,
            branchId: cs.branch?._id,
            systemId: cs.system?._id,
          });
        }

        const g = map.get(key)!;
        const subCategoriesTotal = (cs.subCategories || []).reduce(
          (s: number, sc: any) => s + (sc.marks || 0),
          0
        );
        const termsPercentTotal = (cs.termPercentages || []).reduce(
          (s: number, tp: any) => s + (tp.percentage || 0),
          0
        );
        // One card entry per SUBJECT, not per row. Rows are stored per section,
        // so a class with three sections returns the same subject three times;
        // pushing each would show "33 subjects" for an 11-subject class and
        // triple the total. The sections all carry identical marks (the bulk
        // save writes them together), so the first row represents them all.
        // structureIds collects the rest, because Delete has to remove every
        // section's row, not just one.
        const existing = g.subjects.find(s => s.courseId === cs.course._id);
        if (existing) {
          existing.structureIds.push(cs._id);
          continue;
        }

        g.subjects.push({
          structureId: cs._id,
          structureIds: [cs._id],
          courseId: cs.course._id,
          courseName: cs.course.title,
          courseCode: cs.course.courseIdentifier,
          displayOrder: cs.course.displayOrder ?? 999,
          totalMarks: cs.totalMarks || 0,
          subCategories: (cs.subCategories || []).map((sc: any) => ({
            // Keep the id — marks are stored against it.
            _id: sc._id,
            description: sc.description || "",
            marks: Number(sc.marks) || 0,
          })),
          subCategoriesTotal,
          termsPercentTotal,
          inTotal: cs.includeInTotal !== false,
          gradeOnly: !!cs.showGradeOnly,
        });
        // Matches services/tabulation.js's real total (only counts subjects
        // with includeInTotal !== false) -- this card was summing every
        // subject unconditionally, showing grade-only subjects' marks (PE,
        // Nazra, Art etc.) as part of the class total when they're excluded
        // from the actual report-card math.
        if (cs.includeInTotal !== false) g.totalMarks += cs.totalMarks || 0;
      }

      const groups = Array.from(map.values());
      groups.forEach((g) => g.subjects.sort((a, b) => a.displayOrder - b.displayOrder));
      groups.sort((a, b) => (a.cls.order ?? 999) - (b.cls.order ?? 999));
      setAllocations(groups);
    } catch {
      toast.error("Failed to load marks allocations");
    } finally {
      setLoading(false);
    }
  };

  // Reference data only. The allocation list itself is NOT fetched here --
  // it waits for Search.
  useEffect(() => {
    Promise.all([
      getAllSystems().then((r) => setSystems((r as any).data || [])),
      // all=true because the picker spans campuses; `system` arrives populated
      // as an object, so normalise it to an id or every `c.system === draftSystem`
      // compares an object to a string and silently empties the Class list.
      getAllClasses({ limit: 200, all: "true" }).then((r) =>
        setAllClasses(
          (r.classes || []).map((c: any) => ({
            _id: c._id,
            name: c.name,
            system: c.system?._id || c.system,
          }))
        )
      ),
      // Breakdowns are NOT loaded here. The API falls back to the header
      // campus when no branchId is passed, so a single mount-time fetch only
      // ever returned the header campus's exams -- leaving the Exam dropdown
      // empty whenever you picked a different campus. They are fetched per
      // selected campus in the two effects below instead.
      getAllSessions({ all: true, limit: 200 }).then((r: any) =>
        setSessions(
          ((r?.sessions || r?.data || []) as any[]).map(s => ({
            ...s,
            branch: s.branch?._id || s.branch,
          }))
        )
      ),
      listGroups().then((r: any) =>
        setAllGroups(
          ((r?.data || []) as any[]).map(g => ({
            _id: g._id,
            name: g.name,
            system: g.system?._id || g.system,
            classes: (g.classes || []).map((c: any) => c?._id || c),
          }))
        )
      ),
    ]).finally(() => setLoading(false));
  }, []);

  // "inactive" is a terminal status (an admin's deliberate way of retiring
  // a breakdown, usually a duplicate created by mistake) -- the API returns
  // every status when none is requested, so without this an inactive
  // breakdown keeps showing as a selectable "Exam" indefinitely, often
  // alongside the real one it was retired in favor of (looks like
  // duplicate entries with an identical title).
  const excludeInactive = (list: any[]) => list.filter(b => b.status !== "inactive");

  // Exams for the campus chosen in the FILTER BAR.
  useEffect(() => {
    if (!draftBranch) {
      setBreakdowns([]);
      return;
    }
    getAllResultBreakdowns({ limit: 200, branchId: draftBranch })
      .then(r => setBreakdowns(excludeInactive((r.data as any[]) || [])))
      .catch(() => setBreakdowns([]));
  }, [draftBranch]);

  // Exams for the campus chosen in the ADD/EDIT MODAL. Kept separate because
  // the modal can point at a different campus than the filter bar.
  useEffect(() => {
    if (!form.branchId) {
      setModalBreakdownList([]);
      return;
    }
    getAllResultBreakdowns({ limit: 200, branchId: form.branchId })
      .then(r => setModalBreakdownList(excludeInactive((r.data as any[]) || [])))
      .catch(() => setModalBreakdownList([]));
  }, [form.branchId]);

  // Re-run the search when the applied filters change, never before.
  useEffect(() => {
    if (!hasSearched) return;
    fetchAllocations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasSearched, appliedFilters]);

  // Each level clears the ones below it, so a stale pick from a previous
  // campus or board can never linger and quietly filter out everything.
  const setDraftFilter = (level: string, value: string) => {
    if (level === "branch") {
      setDraftBranch(value);
      setDraftSession(""); setDraftSystem(""); setDraftClass(""); setDraftGroup(""); setDraftBreakdown("");
    } else if (level === "session") {
      setDraftSession(value); setDraftBreakdown("");
    } else if (level === "system") {
      setDraftSystem(value); setDraftClass(""); setDraftGroup(""); setDraftBreakdown("");
    } else if (level === "class") {
      setDraftClass(value); setDraftGroup(""); setDraftBreakdown("");
    } else if (level === "classGroup") {
      setDraftGroup(value);
    } else {
      setDraftBreakdown(value);
    }
  };

  const sessionsForBranch = useMemo(
    () => sessions.filter((s: any) => String(s.branch) === String(draftBranch)),
    [sessions, draftBranch]
  );

  // Only the boards this campus actually runs, so NextGen's Technical/PNMC
  // never show up under an O-Level campus.
  const systemsForBranch = useMemo(() => {
    const b: any = branches.find(x => x._id === draftBranch);
    const allowed = b?.systems?.map((s: any) => s._id || s);
    return allowed?.length ? systems.filter(s => allowed.includes(s._id)) : systems;
  }, [branches, draftBranch, systems]);

  const classesForFilter = useMemo(
    () => (draftSystem ? allClasses.filter((c: any) => c.system === draftSystem) : []),
    [allClasses, draftSystem]
  );

  // Only the groups that actually apply to the selected class -- most classes
  // have none, so this is usually empty and the dropdown stays out of the way.
  const groupsForFilter = useMemo(
    () =>
      draftClass
        ? allGroups.filter(g => g.system === draftSystem && g.classes.includes(draftClass))
        : [],
    [allGroups, draftSystem, draftClass]
  );

  const breakdownsForFilter = useMemo(
    () =>
      breakdowns.filter((b: any) => {
        if (draftSession && String(b.sessionId?._id || b.sessionId) !== String(draftSession))
          return false;
        if (draftClass) {
          const ids = (b.classes || []).map((c: any) => String(c?._id || c));
          if (ids.length && !ids.includes(String(draftClass))) return false;
        }
        return true;
      }),
    [breakdowns, draftSession, draftClass]
  );

  // The Add modal scopes the same way the filter bar does: campus → session →
  // class → exam, each list narrowed by the pick before it.
  const modalSessions = useMemo(
    () => sessions.filter((s: any) => String(s.branch) === String(form.branchId)),
    [sessions, form.branchId]
  );

  const modalClasses = useMemo(() => {
    const b: any = branches.find(x => x._id === form.branchId);
    const allowed = (b?.systems || []).map((s: any) => String(s._id || s));
    let list = allowed.length
      ? allClasses.filter((c: any) => allowed.includes(String(c.system)))
      : allClasses;

    // Never hide the class being edited. Some campuses hold allocations for
    // classes outside their configured Branch.systems -- Eon has Matric rows
    // (Nursery, Class I, Class V-X) while its Branch.systems lists only
    // O-Level -- and filtering those out left the picker blank on an existing
    // allocation, as if no class were selected.
    if (form.classId && !list.some((c: any) => String(c._id) === String(form.classId))) {
      const current = allClasses.find((c: any) => String(c._id) === String(form.classId));
      if (current) list = [current, ...list];
    }
    return list;
  }, [allClasses, branches, form.branchId, form.classId]);

  const systemNameById = useMemo(
    () => new Map(systems.map(s => [s._id, s.name])),
    [systems]
  );

  // A campus running two or more boards can have the same class name twice
  // (one document per board, e.g. Naseerabad's Matric "Class IX" and AKU
  // "Class IX" are different classes with different subjects) -- confirmed
  // against real data 2026-10-07. Only suffix the board name when that's
  // actually possible, so a single-board campus's picker stays plain.
  const classBoardSuffixesNeeded = useMemo(() => {
    const b: any = branches.find(x => x._id === form.branchId);
    return (b?.systems || []).length > 1;
  }, [branches, form.branchId]);

  // Same "only if the class actually has groups" rule as the filter bar.
  // A plain function, not a memo keyed on form.classId -- the Class picker's
  // own onChange needs this for the class it's ABOUT to switch to, before
  // that state update has landed.
  const groupsForClass = (classId: string) => {
    const cls = allClasses.find((c: any) => c._id === classId);
    return classId ? allGroups.filter(g => g.system === cls?.system && g.classes.includes(classId)) : [];
  };
  const groupsForForm = useMemo(() => groupsForClass(form.classId), [allGroups, allClasses, form.classId]);

  const modalBreakdowns = useMemo(() => {
    const list = modalBreakdownList.filter((b: any) => {
      if (String(b.sessionId?._id || b.sessionId) !== String(form.sessionId)) return false;
      const ids = (b.classes || []).map((c: any) => String(c?._id || c));
      return !ids.length || ids.includes(String(form.classId));
    });

    // Same reason as the class picker: the exam on an existing allocation may
    // not be in this list at all -- imported rows point at a Term rather than
    // a Result Breakdown -- which showed "No exams set up for this class yet"
    // while editing an allocation that plainly has one.
    if (
      editingAllocation?.breakdown?._id &&
      !list.some((b: any) => String(b._id) === String(editingAllocation.breakdown._id))
    ) {
      return [editingAllocation.breakdown as any, ...list];
    }
    return list;
  }, [modalBreakdownList, form.sessionId, form.classId, editingAllocation]);

  const handleSearch = () => {
    if (!draftBranch || !draftSession) {
      toast.error("Pick a campus and session first");
      return;
    }
    setPage(1);
    setAppliedFilters({
      branch: draftBranch,
      session: draftSession,
      system: draftSystem,
      class: draftClass,
      classGroup: draftGroup,
      breakdown: draftBreakdown,
    });
    setHasSearched(true);
  };

  const handleResetFilters = () => {
    setDraftBranch(""); setDraftSession(""); setDraftSystem("");
    setDraftClass(""); setDraftGroup(""); setDraftBreakdown("");
    setAppliedFilters(null);
    setHasSearched(false);
    setAllocations([]);
    setPage(1);
  };

  // The server already applied the filters, so this only paginates.
  const filteredAllocations = useMemo(() => {
    const start = (page - 1) * limit;
    return allocations.slice(start, start + limit);
  }, [allocations, page, limit]);

  const totalFiltered = allocations.length;

  const totalPages = Math.ceil(totalFiltered / limit);

  const updateForm = (key: keyof AddForm, value: string | SubjectConfig[]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const toggleSubjectInTotal = (courseId: string) => {
    setForm((prev) => ({
      ...prev,
      subjects: prev.subjects.map((s) =>
        s.courseId === courseId ? { ...s, inTotal: !s.inTotal } : s
      ),
    }));
  };

  const toggleSubjectGradeOnly = (courseId: string) => {
    setForm((prev) => ({
      ...prev,
      subjects: prev.subjects.map((s) =>
        s.courseId === courseId ? { ...s, gradeOnly: !s.gradeOnly } : s
      ),
    }));
  };

  const loadSubjectsForAllocation = (allocation: AllocationGroup) =>
    allocation.subjects.map((s) => ({ ...s }));

  // Editing used to ONLY show whatever subjects this allocation already had
  // rows for, forever -- a subject ticked in Course Structures AFTER this
  // allocation was first created (e.g. STEAM added partway through the
  // term) would never appear here no matter how many times the admin
  // opened Edit and saved, since nothing re-read Course Structures. Now it
  // merges in: existing subjects keep every mark already entered for them
  // untouched, and any course Course Structures has that this allocation
  // doesn't yet is appended as a fresh (zero marks) row, same shape as a
  // brand new allocation gets.
  const handleEditClick = async (allocation: AllocationGroup) => {
    setEditingAllocation(allocation);
    const existing = loadSubjectsForAllocation(allocation);
    setForm({
      sessionId: allocation.sessionId || "",
      branchId: allocation.branchId || "",
      breakdownId: allocation.breakdown._id,
      classId: allocation.cls._id,
      classGroupId: allocation.classGroup?._id || "",
      subjects: existing,
    });
    // Editing always has its subjects in hand, so the "no subjects" notice
    // must never appear here.
    setSubjectsLoaded(true);
    setModalMode("edit");
    setShowModal(true);

    if (!allocation.sessionId) return;
    try {
      // Scoped to this allocation's own group (or "null" for ungrouped) --
      // without this, editing a Biology allocation would merge in Computer
      // group's subjects too, since both are valid ClassSubject rows for
      // the same class.
      const res = await getAllClassSubjects({
        session: allocation.sessionId,
        class: allocation.cls._id,
        classGroup: allocation.classGroup?._id || "null",
      });
      const currentCourseIds = new Set(existing.map((s) => s.courseId));
      const missing = (res.data || []).filter(
        (r: any) => !currentCourseIds.has(r.course._id)
      );
      if (!missing.length) return;
      setForm((prev) => ({
        ...prev,
        subjects: [
          ...prev.subjects,
          ...missing.map((r: any, i: number) => ({
            structureId: "",
            structureIds: [],
            courseId: r.course._id,
            courseName: r.course.title,
            courseCode: r.course.courseIdentifier,
            displayOrder: r.displayOrder ?? existing.length + i,
            totalMarks: 0,
            subCategories: [],
            subCategoriesTotal: 0,
            termsPercentTotal: 0,
            inTotal: true,
            gradeOnly: false,
          })),
        ],
      }));
    } catch {
      // Best-effort -- the existing subjects are already shown and
      // editable regardless of whether this refresh succeeds.
    }
  };

  // Which subject card is expanded. Only one at a time, like RMS -- eleven
  // subjects all open at once is unreadable.
  const [openSubject, setOpenSubject] = useState<string | null>(null);

  // What the class adds up to across every subject counted in the total --
  // was summing every subject unconditionally regardless of the "In total"
  // checkbox (s.inTotal), so a grade-only subject still inflated this figure
  // even with "In total" unticked for it.
  const classTotalMarks = useMemo(
    () => form.subjects.reduce((a, s) => a + (s.inTotal ? Number(s.totalMarks) || 0 : 0), 0),
    [form.subjects]
  );

  // Subcategory total is always derived, never stored separately, so the
  // running "Sum: x / y" cannot disagree with the rows above it.
  const sumSubs = (subs: { marks: number }[]) =>
    subs.reduce((a, s) => a + (Number(s.marks) || 0), 0);

  const editSubject = (
    courseId: string,
    change: (s: SubjectConfig) => SubjectConfig
  ) =>
    setForm(prev => ({
      ...prev,
      subjects: prev.subjects.map(s => {
        if (s.courseId !== courseId) return s;
        const next = change(s);
        return { ...next, subCategoriesTotal: sumSubs(next.subCategories) };
      }),
    }));

  const addSubCategory = (courseId: string) =>
    editSubject(courseId, s => ({
      ...s,
      subCategories: [...s.subCategories, { description: "", marks: 0 }],
    }));

  const removeSubCategory = (courseId: string, index: number) =>
    editSubject(courseId, s => ({
      ...s,
      subCategories: s.subCategories.filter((_, i) => i !== index),
    }));

  const setSubCategory = (
    courseId: string,
    index: number,
    field: "description" | "marks",
    value: string
  ) =>
    editSubject(courseId, s => ({
      ...s,
      subCategories: s.subCategories.map((sc, i) =>
        i !== index
          ? sc
          : { ...sc, [field]: field === "marks" ? Number(value) || 0 : value }
      ),
    }));

  const setSubjectMarks = (courseId: string, raw: string) => {
    const value = raw === "" ? 0 : Number(raw);
    if (!Number.isFinite(value) || value < 0) return;
    setForm(prev => ({
      ...prev,
      subjects: prev.subjects.map(s =>
        s.courseId === courseId ? { ...s, totalMarks: value } : s
      ),
    }));
  };

  // For a NEW allocation the subject list comes from Course Structures (which
  // subjects the class takes), not from marks allocation itself -- otherwise
  // there is no way in: the page would only ever show subjects that already
  // had rows. Mirrors RMS, whose editor "reads class_subjects to know" the set.
  const loadSubjectsFromClass = async (sessionId: string, classId: string, classGroup?: string) => {
    if (!sessionId || !classId) return;
    try {
      const res = await getAllClassSubjects({
        session: sessionId,
        class: classId,
        // Omitted entirely for an ungrouped class, so the query behaves
        // exactly as it always did -- only a grouped class passes this,
        // scoping the subject list to the one group picked.
        ...(classGroup ? { classGroup } : {}),
      });
      const rows = res.data || [];
      // Reported inline below the picker rather than as a toast: picking a
      // class is browsing, not submitting, and a toast on every empty class
      // interrupts that.
      setSubjectsLoaded(true);
      setForm(prev => ({
        ...prev,
        subjects: rows.map((r, i) => ({
          structureId: "",
          structureIds: [],
          courseId: r.course._id,
          courseName: r.course.title,
          courseCode: r.course.courseIdentifier,
          displayOrder: r.displayOrder ?? i,
          totalMarks: 0,
          subCategories: [],
          subCategoriesTotal: 0,
          termsPercentTotal: 0,
          inTotal: true,
          gradeOnly: false,
        })),
      }));
    } catch {
      toast.error("Failed to load this class's subjects");
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirm) return;
    try {
      // Every section's row for every subject -- a class with 3 sections has 3
      // rows per subject, and deleting only the first would leave the rest
      // behind, still feeding marks entry and tabulation.
      await Promise.all(
        deleteConfirm.subjects.flatMap(s =>
          (s.structureIds?.length ? s.structureIds : [s.structureId]).map(id =>
            deleteCourseStructure(id)
          )
        )
      );
      toast.success("Deleted");
      setDeleteConfirm(null);
      fetchAllocations();
    } catch {
      toast.error("Delete failed");
    }
  };

  // One save path for both new and edit. Previously "new" only toasted "New
  // allocation created" without saving anything, and "edit" could persist just
  // two booleans -- total marks was display-only, so the page could not do the
  // one thing it exists for.
  const handleSave = async () => {
    if (!form.sessionId || !form.branchId || !form.breakdownId || !form.classId) {
      toast.error("Session, campus, exam and class are all required");
      return;
    }
    if (groupsForForm.length > 0 && !form.classGroupId) {
      toast.error("This class is split into groups — pick one before saving.");
      return;
    }
    if (!form.subjects.length) {
      toast.error(
        "This class has no subjects. Assign them in Course Structures first."
      );
      return;
    }

    // Same rule the server enforces, checked here so the message names the
    // subject rather than coming back as a bare API error.
    const mismatched = form.subjects.find(
      s => s.subCategoriesTotal > 0 && Math.abs(s.subCategoriesTotal - s.totalMarks) > 0.001
    );
    if (mismatched) {
      toast.error(
        `${mismatched.courseName}: subcategories total ${mismatched.subCategoriesTotal}, but the subject total is ${mismatched.totalMarks}`
      );
      return;
    }

    setSubmitting(true);
    try {
      const res = await bulkSaveCourseStructures({
        session: form.sessionId,
        class: form.classId,
        classGroup: form.classGroupId || null,
        breakdown: form.breakdownId,
        subjects: form.subjects.map(s => ({
          course: s.courseId,
          totalMarks: s.totalMarks,
          includeInTotal: s.inTotal,
          showGradeOnly: s.gradeOnly,
          // _id goes back untouched for existing rows so their student marks
          // stay attached; rows the user added have none and get a fresh one.
          subCategories: s.subCategories.map(sc => ({
            ...(sc._id ? { _id: sc._id } : {}),
            description: sc.description,
            marks: Number(sc.marks) || 0,
          })),
        })),
      });
      toast.success(res.message || "Allocation saved");
      // Point the list at what was just saved and load it, so the work is
      // visible immediately -- otherwise a first-time save lands behind an
      // unsearched page and looks like nothing happened.
      const applied = {
        branch: form.branchId,
        session: form.sessionId,
        system: "",
        class: form.classId,
        classGroup: "",
        breakdown: form.breakdownId,
      };
      setDraftBranch(form.branchId);
      setDraftSession(form.sessionId);
      setDraftClass("");
      setDraftGroup("");
      setDraftBreakdown("");
      setAppliedFilters(applied);
      setHasSearched(true);
      fetchAllocations(applied);
      setShowModal(false);
      setModalMode("new");
      setForm(EMPTY_FORM);
      setEditingAllocation(null);
    } catch (e: any) {
      toast.error(e?.response?.data?.error || "Failed to save allocation");
    } finally {
      setSubmitting(false);
    }
  };

  const openNewModal = () => {
    setModalMode("new");
    setEditingAllocation(null);
    setForm(EMPTY_FORM);
    setSubjectsLoaded(false);
    setShowModal(true);
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        {/* Header */}
        <div className="d-flex align-items-start justify-content-between mb-4">
          <div>
            <h4 className="mb-2">Marks allocation</h4>
            <p className="text-muted small mb-0">
              One row per exam → class. Pick campus → session, then Search. To
              add an allocation, choose campus → session → class → exam and set
              the total marks for every subject that class takes.
            </p>
          </div>
          <button
            className="btn btn-dark btn-sm"
            onClick={openNewModal}
          >
            + Add
          </button>
        </div>

        {/* Filters -- cascading, and nothing loads until Search, matching
            Defaulters and Fee Sticker Printing. Campus comes FIRST because
            sessions here belong to a campus: every campus has its own
            "Aug 2026-2027", so a session cannot be chosen before one is. */}
        {/* No align-items-end here: the columns are different heights because
            only some carry help text, and bottom-aligning them staircases the
            dropdowns. Aligning at the top keeps every label and every select on
            one line, with the hint hanging below where it applies. */}
        <div className="row mb-3 g-3">
          <div className="col-md-2">
            <label className="form-label mb-1">Campus</label>
            <select
              className="form-select"
              value={draftBranch}
              onChange={e => setDraftFilter("branch", e.target.value)}
            >
              <option value="">— Select campus —</option>
              {branches.map(b => (
                <option key={b._id} value={b._id}>{b.name}</option>
              ))}
            </select>
          </div>

          <div className="col-md-2">
            <label className="form-label mb-1">Session</label>
            <select
              className="form-select"
              value={draftSession}
              disabled={!draftBranch}
              onChange={e => setDraftFilter("session", e.target.value)}
            >
              <option value="">— Select session —</option>
              {sessionsForBranch.map(s => (
                <option key={s._id} value={s._id}>{s.title}</option>
              ))}
            </select>
            {!draftBranch && <div className="form-text">Pick a campus first.</div>}
          </div>

          <div className="col-md-2">
            <label className="form-label mb-1">Board (optional)</label>
            <select
              className="form-select"
              value={draftSystem}
              disabled={!draftBranch}
              onChange={e => setDraftFilter("system", e.target.value)}
            >
              <option value="">— All boards —</option>
              {systemsForBranch.map(s => (
                <option key={s._id} value={s._id}>{s.name}</option>
              ))}
            </select>
          </div>

          <div className="col-md-2">
            <label className="form-label mb-1">Class (optional)</label>
            <select
              className="form-select"
              value={draftClass}
              disabled={!draftSystem}
              onChange={e => setDraftFilter("class", e.target.value)}
            >
              <option value="">— All classes —</option>
              {classesForFilter.map(c => (
                <option key={c._id} value={c._id}>{c.name}</option>
              ))}
            </select>
            {!draftSystem && <div className="form-text">Pick a board first.</div>}
          </div>

          {/* Only rendered once a class with real groups is picked -- most
              classes have none, so this stays out of the way until it's
              actually relevant (e.g. Grade IX/X split into Biology/Computer). */}
          {groupsForFilter.length > 0 && (
            <div className="col-md-2">
              <label className="form-label mb-1">Group (optional)</label>
              <select
                className="form-select"
                value={draftGroup}
                onChange={e => setDraftFilter("classGroup", e.target.value)}
              >
                <option value="">— All groups —</option>
                {groupsForFilter.map(g => (
                  <option key={g._id} value={g._id}>{g.name}</option>
                ))}
              </select>
            </div>
          )}

          <div className="col-md-2">
            <label className="form-label mb-1">Exam (optional)</label>
            <select
              className="form-select"
              value={draftBreakdown}
              disabled={!draftSession}
              onChange={e => setDraftFilter("breakdown", e.target.value)}
            >
              <option value="">— All exams —</option>
              {breakdownsForFilter.map(b => (
                <option key={b._id} value={b._id}>{b.title}</option>
              ))}
            </select>
          </div>

          <div className="col-md-2">
            {/* Empty label so the buttons line up with the dropdowns rather
                than with the labels above them. */}
            <label className="form-label mb-1 d-block invisible">Actions</label>
            <div className="d-flex gap-2">
              <button
                className="btn btn-primary flex-grow-1"
                onClick={handleSearch}
                disabled={!draftBranch || !draftSession}
              >
                Search
              </button>
              <button className="btn btn-light" onClick={handleResetFilters}>
                Reset
              </button>
            </div>
          </div>
        </div>

        {/* List */}
        {loading ? (
          <div className="text-center py-5 text-muted">Loading...</div>
        ) : !hasSearched ? (
          <div className="text-center py-5 text-muted">
            Choose a campus and session above, then click <strong>Search</strong>.
            <div className="small mt-1">
              Board, class and exam are optional — leave them to see everything
              in that session.
            </div>
          </div>
        ) : filteredAllocations.length === 0 ? (
          <div className="text-center py-5 text-muted">
            No marks allocations found for this selection.
            <div className="small mt-1">
              Use <strong>+ Add</strong> to allocate marks for a class.
            </div>
          </div>
        ) : (
          <>
            {filteredAllocations.map((a) => (
              <div key={a.key} className="border rounded mb-3 p-3">
                <div className="d-flex align-items-center justify-content-between mb-3">
                  <div className="d-flex gap-2 align-items-center">
                    <small className="badge text-white" style={{ background: "#555" }}>
                      S1DH
                    </small>
                    <strong>{a.cls.name}</strong>
                    {a.classGroup && (
                      <span className="badge bg-info-subtle text-info-emphasis border border-info-subtle">
                        {a.classGroup.name}
                      </span>
                    )}
                    <span className="text-muted small">{a.breakdown.title}</span>
                    <span className="text-muted small">{a.breakdown.startDate?.substring(0, 4)}</span>
                  </div>
                  <div className="flex-shrink-0 d-flex gap-2">
                    <button
                      className="btn btn-link btn-sm p-0 text-primary text-decoration-none"
                      onClick={() => handleEditClick(a)}
                    >
                      Edit
                    </button>
                    <button
                      className="btn btn-link btn-sm p-0 text-danger text-decoration-none"
                      onClick={() => setDeleteConfirm(a)}
                    >
                      Delete
                    </button>
                  </div>
                </div>

                <div className="row text-muted small g-3">
                  <div className="col-md-2">
                    <div className="mb-1 text-uppercase">Subjects configured</div>
                    <div className="fw-bold text-dark">{a.subjects.length}</div>
                  </div>
                  <div className="col-md-2">
                    <div className="mb-1 text-uppercase">Counted in total</div>
                    <div className="fw-bold text-dark">
                      {a.subjects.filter((s) => s.inTotal).length} / {a.subjects.length}
                    </div>
                  </div>
                  <div className="col-md-2">
                    <div className="mb-1 text-uppercase">Total marks</div>
                    <div className="fw-bold text-dark">{a.totalMarks.toFixed(2)}</div>
                  </div>
                  <div className="col-md-6">
                    <div className="mb-1 text-uppercase">Subject mix</div>
                    <div className="small">
                      {a.subjects.slice(0, 3).map((s) => s.courseName.toUpperCase()).join(", ")}
                      {a.subjects.length > 3 && `, +${a.subjects.length - 3} more`}
                    </div>
                  </div>
                </div>
              </div>
            ))}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="d-flex align-items-center justify-content-end gap-2 mt-4">
                <button
                  className="btn btn-sm btn-outline-secondary"
                  onClick={() => setPage(Math.max(1, page - 1))}
                  disabled={page === 1}
                >
                  Prev
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                  <button
                    key={p}
                    className={`btn btn-sm ${
                      p === page ? "btn-primary" : "btn-outline-secondary"
                    }`}
                    onClick={() => setPage(p)}
                  >
                    {p}
                  </button>
                ))}
                <button
                  className="btn btn-sm btn-outline-secondary"
                  onClick={() => setPage(Math.min(totalPages, page + 1))}
                  disabled={page === totalPages}
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="modal show d-block" style={{ background: "rgba(0,0,0,0.5)" }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header border-0 pb-0">
                <h5 className="modal-title">Delete allocation</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setDeleteConfirm(null)}
                ></button>
              </div>
              <div className="modal-body">
                <p>Delete marks allocation for <strong>{deleteConfirm.cls.name}</strong>? This cannot be undone.</p>
              </div>
              <div className="modal-footer border-0 d-flex gap-2 justify-content-end">
                <button
                  className="btn btn-secondary"
                  onClick={() => setDeleteConfirm(null)}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-danger"
                  onClick={handleDeleteConfirm}
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="modal show d-block" style={{ background: "rgba(0,0,0,0.5)" }}>
          <div className="modal-dialog modal-dialog-centered modal-lg">
            <div className="modal-content">
              <div className="modal-header border-0 pb-1">
                <h5 className="modal-title">{modalMode === "new" ? "New" : "Edit"} allocation</h5>
              </div>
              <div className="modal-body">
                {/* Campus → Session → Class → Exam, each scoped to the one
                    before it. Previously all four listed everything in the
                    school, so picking Eon still offered Naseerabad's exams, and
                    Session came before Campus even though a session belongs to
                    exactly one campus. */}
                <p className="text-muted small mb-3">Pick the scope</p>
                <div className="row g-3 mb-3">
                  <div className="col-6">
                    <label className="form-label">Campus</label>
                    <select
                      className="form-select"
                      value={form.branchId}
                      // Scope is fixed once an allocation exists: changing it
                      // here would save onto a different class entirely rather
                      // than edit this one.
                      disabled={modalMode === "edit"}
                      onChange={(e) => {
                        updateForm("branchId", e.target.value);
                        updateForm("sessionId", "");
                        updateForm("classId", "");
                        updateForm("breakdownId", "");
                        updateForm("subjects", []);
                        setSubjectsLoaded(false);
                      }}
                    >
                      <option value="">— Select campus —</option>
                      {branches.map((b) => (
                        <option key={b._id} value={b._id}>
                          {b.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-6">
                    <label className="form-label">Session</label>
                    <select
                      className="form-select"
                      value={form.sessionId}
                      disabled={!form.branchId || modalMode === "edit"}
                      onChange={(e) => {
                        // Class subjects are per SESSION, so changing session
                        // invalidates the class and everything loaded for it.
                        // Leaving the class selected showed subjects from the
                        // old session, or none at all.
                        updateForm("sessionId", e.target.value);
                        updateForm("classId", "");
                        updateForm("breakdownId", "");
                        updateForm("subjects", []);
                        setSubjectsLoaded(false);
                      }}
                    >
                      <option value="">— Select session —</option>
                      {modalSessions.map((s) => (
                        <option key={s._id} value={s._id}>
                          {s.title}
                        </option>
                      ))}
                    </select>
                    {!form.branchId && (
                      <small className="text-muted">Pick a campus first.</small>
                    )}
                  </div>
                </div>

                <div className="mb-3">
                  <label className="form-label">Class</label>
                  <select
                    className="form-select"
                    value={form.classId}
                    disabled={!form.sessionId || modalMode === "edit"}
                    onChange={(e) => {
                      const classId = e.target.value;
                      const groups = groupsForClass(classId);
                      const isEditingThisClass = editingAllocation && classId === editingAllocation.cls._id;
                      setForm(prev => ({
                        ...prev,
                        classId,
                        classGroupId: isEditingThisClass ? editingAllocation.classGroup?._id || "" : "",
                        // Editing: keep the marks already allocated. New +
                        // ungrouped: pull what Course Structures gives it
                        // below. New + grouped: nothing until a group is
                        // picked -- loading now would mix every group's
                        // subjects together with no way to tell them apart.
                        subjects: isEditingThisClass ? loadSubjectsForAllocation(editingAllocation) : [],
                      }));
                      if (!isEditingThisClass && classId && groups.length === 0) {
                        loadSubjectsFromClass(form.sessionId, classId);
                      } else if (!isEditingThisClass) {
                        setSubjectsLoaded(false);
                      }
                    }}
                  >
                    <option value="">— Select class —</option>
                    {modalClasses.map((c) => (
                      <option key={c._id} value={c._id}>
                        {/* A campus running more than one board can have TWO
                            separate "Class IX" documents, one per board (e.g.
                            Naseerabad's Matric and AKU both have their own) --
                            same name, different _id, different subjects.
                            Suffixing the board name is the only way to tell
                            them apart here; without it they look like an
                            exact duplicate in the list. */}
                        {c.name}
                        {classBoardSuffixesNeeded && systemNameById.get(c.system || "") ? ` — ${systemNameById.get(c.system || "")}` : ""}
                      </option>
                    ))}
                  </select>
                  {!form.sessionId && (
                    <small className="text-muted">Pick a session first.</small>
                  )}
                </div>

                {groupsForForm.length > 0 && (
                  <div className="mb-3">
                    <label className="form-label">Group</label>
                    <select
                      className="form-select"
                      value={form.classGroupId}
                      disabled={modalMode === "edit"}
                      onChange={(e) => {
                        const classGroupId = e.target.value;
                        updateForm("classGroupId", classGroupId);
                        if (classGroupId) {
                          loadSubjectsFromClass(form.sessionId, form.classId, classGroupId);
                        } else {
                          updateForm("subjects", []);
                          setSubjectsLoaded(false);
                        }
                      }}
                    >
                      <option value="">— Select group —</option>
                      {groupsForForm.map((g) => (
                        <option key={g._id} value={g._id}>{g.name}</option>
                      ))}
                    </select>
                    <small className="text-muted">
                      This class is split into groups — each group's subjects are allocated separately.
                    </small>
                  </div>
                )}

                <div className="mb-3">
                  <label className="form-label">Exam</label>
                  <select
                    className="form-select"
                    value={form.breakdownId}
                    disabled={!form.classId || modalMode === "edit"}
                    onChange={(e) => updateForm("breakdownId", e.target.value)}
                  >
                    <option value="">— Select exam —</option>
                    {modalBreakdowns.map((b) => (
                      <option key={b._id} value={b._id}>
                        {b.title}
                      </option>
                    ))}
                  </select>
                  {!form.classId && (
                    <small className="text-muted">Pick a class first.</small>
                  )}
                  {form.classId && modalBreakdowns.length === 0 && (
                    <small className="text-danger">
                      No exams set up for this class yet — create one in Result
                      Breakdowns first.
                    </small>
                  )}
                </div>

                {/* alert-light + text-dark rather than alert-warning: this
                    template renders warning alerts as near-yellow text on a
                    pale yellow ground, which is unreadable. */}
                {form.classId && subjectsLoaded && form.subjects.length === 0 && (
                  <div className="alert alert-light border text-dark py-2 small mb-0">
                    This class has no subjects assigned yet. Add them in{" "}
                    <strong>Course Structures</strong> first — marks are
                    allocated against the subjects a class takes.
                  </div>
                )}

                {/* Subject configuration.
                    Each subject is its own bordered, collapsible card, matching
                    RMS. They previously ran together as identical rows with no
                    separation, so there was no way to tell where one ended.
                    padding-right on the scroller keeps the checkbox column clear
                    of the scrollbar, which was clipping "Hide marks on report
                    card". */}
                {form.subjects.length > 0 && (
                  <div className="mt-4 pt-3 border-top">
                    <div className="d-flex align-items-center justify-content-between mb-3">
                      <p className="small text-muted mb-0">
                        <strong>
                          {form.subjects.length} subject
                          {form.subjects.length !== 1 ? "s" : ""}
                        </strong>{" "}
                        in this class
                      </p>
                      <span className="small text-muted">
                        Class total: <strong>{classTotalMarks.toFixed(2)}</strong>
                      </span>
                    </div>

                    <div style={{ maxHeight: 380, overflowY: "auto", paddingRight: 10 }}>
                      {form.subjects.map(subject => {
                        const open = openSubject === subject.courseId;
                        const subsTotal = subject.subCategoriesTotal;
                        const mismatch =
                          subject.subCategories.length > 0 &&
                          Math.abs(subsTotal - subject.totalMarks) > 0.001;

                        return (
                          <div
                            key={subject.courseId}
                            className={`border rounded mb-2 ${mismatch ? "border-danger" : ""}`}
                          >
                            {/* Header row — always visible */}
                            <div className="d-flex align-items-center gap-2 px-3 py-2 bg-light">
                              <button
                                type="button"
                                className="btn btn-sm btn-link p-0 text-muted"
                                style={{ width: 18 }}
                                aria-expanded={open}
                                aria-label={open ? "Collapse" : "Expand"}
                                onClick={() =>
                                  setOpenSubject(open ? null : subject.courseId)
                                }
                              >
                                {open ? "▾" : "▸"}
                              </button>

                              <div className="flex-grow-1 min-w-0">
                                <span className="fw-bold small">
                                  {subject.courseName.toUpperCase()}
                                </span>{" "}
                                <span className="badge bg-white text-dark border">
                                  {subject.courseCode}
                                </span>
                                <span className="small text-muted ms-2">
                                  Total: <strong>{subject.totalMarks.toFixed(2)}</strong>
                                </span>
                                {subject.subCategories.length > 0 && (
                                  <span
                                    className={`small ms-2 ${mismatch ? "text-danger fw-semibold" : "text-muted"}`}
                                  >
                                    Subcats: {subsTotal.toFixed(2)}
                                  </span>
                                )}
                                {subject.termsPercentTotal > 0 && (
                                  <span className="small text-muted ms-2">
                                    Terms: {subject.termsPercentTotal.toFixed(2)}%
                                  </span>
                                )}
                              </div>

                              <div className="flex-shrink-0 d-flex gap-3">
                                <div className="form-check mb-0">
                                  <input
                                    type="checkbox"
                                    className="form-check-input"
                                    id={`inTotal_${subject.courseId}`}
                                    checked={subject.inTotal}
                                    onChange={() => toggleSubjectInTotal(subject.courseId)}
                                  />
                                  <label
                                    className="form-check-label small"
                                    htmlFor={`inTotal_${subject.courseId}`}
                                  >
                                    In total
                                  </label>
                                </div>
                                <div className="form-check mb-0">
                                  <input
                                    type="checkbox"
                                    className="form-check-input"
                                    id={`gradeOnly_${subject.courseId}`}
                                    checked={subject.gradeOnly}
                                    onChange={() => toggleSubjectGradeOnly(subject.courseId)}
                                  />
                                  <label
                                    className="form-check-label small"
                                    htmlFor={`gradeOnly_${subject.courseId}`}
                                  >
                                    Grade only
                                    <span className="d-block text-muted" style={{ fontSize: ".7rem" }}>
                                      Hide marks on report card
                                    </span>
                                  </label>
                                </div>
                              </div>
                            </div>

                            {/* Body — total marks and subcategories */}
                            {open && (
                              <div className="px-3 py-3">
                                <div className="row g-3 mb-3">
                                  <div className="col-md-4">
                                    <label
                                      className="form-label small mb-1"
                                      htmlFor={`total_${subject.courseId}`}
                                    >
                                      Total marks
                                    </label>
                                    <input
                                      id={`total_${subject.courseId}`}
                                      type="number"
                                      min={0}
                                      step="0.01"
                                      className="form-control form-control-sm"
                                      value={subject.totalMarks}
                                      onChange={e =>
                                        setSubjectMarks(subject.courseId, e.target.value)
                                      }
                                    />
                                  </div>
                                </div>

                                <div className="d-flex align-items-center justify-content-between mb-2">
                                  <span className="small fw-semibold">Subcategories</span>
                                  <span
                                    className={`small ${mismatch ? "text-danger fw-semibold" : "text-muted"}`}
                                  >
                                    Sum: {subsTotal.toFixed(2)} / {subject.totalMarks.toFixed(2)}
                                  </span>
                                </div>

                                {subject.subCategories.length === 0 ? (
                                  <p className="small text-muted mb-2">
                                    None — the whole subject is marked out of{" "}
                                    {subject.totalMarks.toFixed(2)}. Add subcategories to
                                    split it (e.g. oral and written).
                                  </p>
                                ) : (
                                  <div className="table-responsive">
                                    <table className="table table-sm align-middle mb-2">
                                      <thead>
                                        <tr>
                                          <th className="small">Description</th>
                                          <th className="small" style={{ width: 130 }}>Marks</th>
                                          <th style={{ width: 44 }}></th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {subject.subCategories.map((sc, i) => (
                                          <tr key={i}>
                                            <td>
                                              <input
                                                className="form-control form-control-sm"
                                                value={sc.description}
                                                placeholder="e.g. oral"
                                                onChange={e =>
                                                  setSubCategory(
                                                    subject.courseId,
                                                    i,
                                                    "description",
                                                    e.target.value
                                                  )
                                                }
                                              />
                                            </td>
                                            <td>
                                              <input
                                                type="number"
                                                min={0}
                                                step="0.01"
                                                className="form-control form-control-sm"
                                                value={sc.marks}
                                                onChange={e =>
                                                  setSubCategory(
                                                    subject.courseId,
                                                    i,
                                                    "marks",
                                                    e.target.value
                                                  )
                                                }
                                              />
                                            </td>
                                            <td>
                                              <button
                                                type="button"
                                                className="btn btn-sm btn-link text-danger p-0"
                                                aria-label="Remove subcategory"
                                                onClick={() =>
                                                  removeSubCategory(subject.courseId, i)
                                                }
                                              >
                                                ×
                                              </button>
                                            </td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </div>
                                )}

                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-secondary"
                                  onClick={() => addSubCategory(subject.courseId)}
                                >
                                  + Add subcategory
                                </button>

                                {mismatch && (
                                  <p className="small text-danger mb-0 mt-2">
                                    Subcategories must add up to the subject total before
                                    this can be saved.
                                  </p>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
              <div className="modal-footer border-0 pt-1 d-flex gap-2 justify-content-end">
                <button
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowModal(false);
                    setModalMode("new");
                    setForm(EMPTY_FORM);
                    setEditingAllocation(null);
                  }}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-dark"
                  onClick={handleSave}
                  disabled={
                    submitting ||
                    !form.branchId ||
                    !form.sessionId ||
                    !form.classId ||
                    !form.breakdownId ||
                    !form.subjects.length
                  }
                >
                  {submitting ? "Saving…" : "Save all"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MarksAllocationPage;
